<template>
  <nav
    v-show="segments.length"
    class="hcrumb"
    :class="{ 'hcrumb--compact': compact, 'hcrumb--narrow': narrow, 'hcrumb--notch': notch }"
    aria-label="Brotkrumen"
  >
    <ol class="hcrumb__list">
      <li
        v-for="segment in segments"
        :key="segment.key"
        class="hcrumb__item"
        :class="{ 'hcrumb__item--parent': !segment.current }"
      >
        <span
          class="hcrumb__text"
          :title="segment.title"
          :aria-current="segment.current ? 'page' : null"
        ><span v-if="!compact" class="hcrumb__slash" aria-hidden="true">/</span>{{ segment.title }}</span>
      </li>
    </ol>
  </nav>
</template>

<script>
// Brotkrumen der Hub-Kopfleiste (Mobility-Hub-Analyse „breadcrumb“, Farben aus tokens.css): hinter dem Mandanten
// „/ Gruppe / Seite“ aus navigation.js (findEntry), z. B. „/ Verwaltung / Verkäufe“. Die Gruppe ist reiner Text
// (Gruppen haben keine eigene Seite), die Seite trägt aria-current="page"; Seiten ohne Gruppe (Übersicht, Seiten aus
// EXTRA_TITLES) zeigen nur „/ Seite“, unbekannte Pfade nichts.
// Maße wie im Hub: Texte 14/20 in 500 --ink, Trenner „/“ 16 px --ink-off (aria-hidden, nicht markierbar), Abstand 8;
// Grundlinie und Höhe 24 wie der Mandantenname bzw. die Brotkrumen im Hub.
// Platz: unter 1024 px nur die letzte Ebene; darüber entfällt die Gruppe ganz, sobald nicht beide Ebenen vollständig
// passen (sie bricht in eine abgeschnittene zweite Zeile um und bleibt für Screenreader lesbar). Der Seitentitel kürzt
// mit „…“ (voller Text im title); der Trenner steht im Textfeld und wird mitgekürzt, nie allein. Unter 44 px Breite
// (compact 28) ist die Navigation unsichtbar statt „/…“. Ab 768 px höchstens calc(50vw − 230px) breit, damit nichts
// unter die Notch läuft; zusätzlich begrenzt AppHubTopbar ihren linken Teil auf calc(50 % − 130px).
// Prop compact (unter 768 px): nur der Seitentitel, ohne Trenner. Prop notch (Standard true): Breitengrenze für die
// Notch; die Systemleiste der klassischen Shell (AppSystemBar) setzt sie auf false. Keine Ereignisse, keine Slots.
import { findEntry } from '../../shell/navigation';

// darunter zeigte die Navigation nur noch „/…“ bzw. „…“
const MIN_WIDTH = 44;
const MIN_WIDTH_COMPACT = 28;

export default {
  name: 'AppHubBreadcrumb',
  props: {
    compact: { type: Boolean, default: false },
    // ab 768 px nie unter die Notch laufen (nur in der Hub-Kopfleiste)
    notch: { type: Boolean, default: true }
  },
  data() {
    return { width: null };
  },
  computed: {
    // [{ key, title, current }], reaktiv auf den Routenwechsel
    segments() {
      const found = findEntry(this.$route.path);
      if (!found.entry) {
        return [];
      }
      const list = [];
      // Hauptpunkte ohne Kinder (Übersicht) sind zugleich group und entry
      if (found.group && found.group !== found.entry) {
        list.push({ key: 'gruppe', title: found.group.title, current: false });
      }
      list.push({ key: 'seite', title: found.entry.title, current: true });
      return this.compact ? list.slice(-1) : list;
    },
    narrow() {
      return this.width !== null && this.width < (this.compact ? MIN_WIDTH_COMPACT : MIN_WIDTH);
    }
  },
  mounted() {
    // Breite beobachten; „narrow“ blendet nur per visibility aus, die Breite selbst ändert sich dadurch nicht
    if (typeof window.ResizeObserver === 'function') {
      this.observer = new window.ResizeObserver((entries) => {
        const entry = entries[entries.length - 1];
        this.width = entry ? entry.contentRect.width : null;
      });
      this.observer.observe(this.$el);
    }
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
};
</script>

<style scoped>
/* Jede Ebene der Flex-Kette darf schrumpfen (min-width 0), sonst kürzt der Text nicht. Keine bedienbaren Elemente,
   daher darf die Navigation überstehende Reste abschneiden. */
.hcrumb {
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  font-family: var(--font);
}
/* Eine sichtbare Zeile (24): row-reverse mit order legt die Seite zuerst, die Gruppe danach; passt nicht beides,
   bricht die Gruppe in die abgeschnittene zweite Zeile um. justify-content flex-end packt (bei row-reverse) links.
   Vuetify setzt .v-application ol { padding-left: 24px } (0,1,1); die Klasse mit Scope-Attribut wiegt mehr. */
.hcrumb__list {
  display: flex;
  flex-direction: row-reverse;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-content: flex-start;
  align-items: center;
  gap: 0 8px;
  flex: 1 1 auto;
  min-width: 0;
  height: 24px;
  margin: 0;
  padding: 0;
  overflow: hidden;
  list-style: none;
}
.hcrumb--narrow .hcrumb__list {
  visibility: hidden;
}
/* volle Zeilenhöhe 24, damit der Text (20) mittig steht: im umbrechenden Flex ist die Zeile so hoch wie ihre Einträge */
.hcrumb__item {
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  order: 1;
  min-width: 0;
  height: 24px;
}
.hcrumb__item--parent {
  order: 2;
}
.hcrumb__text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ink);
}
/* Zeilenhöhe 16 statt der 24 im Hub: der Trenner steht im Textfeld und darf dessen Zeile nicht vergrößern, sonst
   rutscht der Text unter die Grundlinie des Mandantennamens */
.hcrumb__slash {
  margin-right: 8px;
  font-size: 16px;
  line-height: 16px;
  font-weight: 400;
  color: var(--ink-off);
  -webkit-user-select: none;
  user-select: none;
}
/* Desktop: nie unter die Notch (240 breit, mittig am Fenster) */
@media (min-width: 768px) {
  .hcrumb.hcrumb--notch:not(.hcrumb--compact) {
    max-width: calc(50vw - 230px);
  }
}
/* unter 1024 px nur die letzte Ebene */
@media (max-width: 1023.98px) {
  .hcrumb__item--parent {
    display: none;
  }
}
</style>
